import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { jobsApi } from '../../api/jobs'
import { useAuth } from '../../auth/useAuth'
import { useCompanies } from '../../companies/useCompanies'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { IMPORT_STEPS } from './steps'
import { ErrorState, LoadingState } from '../../components/States'
import { isJobLive, useJobPolling } from '../../hooks/useJobPolling'
import { formatInt } from '../../lib/format'

/* Step 5, ported from "batchbooks_import_success_state".

   Previously this page redirected to the job detail screen the moment the job
   left a live status. It now holds the prototype's success state instead — but
   only for a clean Completed run. PartialSuccess and Failed still hand off to
   /app/jobs/:id, because that screen has the per-row errors and the Fix centre,
   and a green tick would be actively misleading there. */
export function ImportRunPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const { current } = useCompanies()
  const { job, error, loading } = useJobPolling(jobId)

  const settled = job && !isJobLive(job.status)
  const clean = job?.status === 'Completed'

  useEffect(() => {
    if (!settled) return
    void refreshUser()
    if (!clean && job) navigate(`/app/jobs/${job.id}`, { replace: true })
  }, [settled, clean, job, navigate, refreshUser])

  if (loading && !job) return <LoadingState label="Queuing your import…" />
  if (error && !job) return <ErrorState error={error} />
  if (!job) return null

  if (clean) {
    return (
      <div className="bb bb-wizard">
        <BbStepper steps={IMPORT_STEPS} current={5} />

        <div className="bb-success">
          <span className="bb-success-ico">
            <BbIcon name="check-circle" />
          </span>
          <h2>Import Successful!</h2>
          <p>
            Your data has been successfully imported to{' '}
            <b>{job.companyName ?? current?.companyName ?? 'QuickBooks'}</b>.{' '}
            {formatInt(job.successRows)} records were processed without errors.
          </p>
          <span className="bb-jobchip">
            <b>Job ID:</b>
            <span>#{job.id}</span>
          </span>
        </div>

        <div className="bb-wizard-foot">
          <span className="bb-statusline">
            <span className="bb-sum-k">Status</span>
            <i />
            Completed
          </span>
          <span className="bb-wizard-foot-r">
            <Link className="bb-btn bb-btn-quiet" to="/app">
              Done
            </Link>
            <Link className="bb-btn bb-btn-navy" to="/app/jobs">
              View Job History
            </Link>
          </span>
        </div>
      </div>
    )
  }

  const total = job.totalRows || 1
  const processed = job.processedRows ?? job.successRows + job.errorRows + job.skippedRows
  const percent = Math.min(100, Math.round((processed / total) * 100))

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={4} />

      <div className="bb-success">
        <span className="bb-success-ico">
          <BbIcon name="upload" />
        </span>
        <h2>
          Importing {formatInt(job.totalRows)} {job.entityType.toLowerCase()} rows
        </h2>
        <p>
          {job.status === 'Pending'
            ? 'Waiting for a worker to pick the job up.'
            : 'Writing through the QuickBooks batch API.'}{' '}
          You can close this tab — the job keeps running and the result will be in Job History.
        </p>

        <div className="prog" style={{ marginTop: 28, marginBottom: 12 }}>
          <i style={{ width: `${percent}%` }} />
        </div>
        <div className="bb-map-help">
          {formatInt(processed)} of {formatInt(job.totalRows)} processed · {percent}%
        </div>
      </div>

      <div className="bb-wizard-foot">
        <button
          className="bb-btn bb-btn-quiet"
          type="button"
          disabled={job.status === 'CancellationRequested'}
          onClick={() => void jobsApi.cancel(job.id)}
        >
          {job.status === 'CancellationRequested' ? 'Cancelling…' : 'Cancel job'}
        </button>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-quiet" to="/app">
            Back to dashboard
          </Link>
        </span>
      </div>
    </div>
  )
}
